/**
 * The retro kit's dialogue window (slice 6b): a `bottom` panel with the
 * speaker's name on its own line (`--ab-name`, none for a line nobody says),
 * the page's text below it, and a blinking ▼ (`ui.dialogue.more`) at the right
 * while more pages follow. Text appears at once (no typewriter; the blink's
 * period is `uiTiming.dialoguePageMs`). The shell keeps the pages (string keys
 * it resolved) and which one shows; this draws one page.
 *
 * Input: Enter, Space or E advances, Escape or Backspace closes (keys in the
 * capture phase, marked handled; Tab stays in the window, which holds the
 * focus while it is open and gives it back after), the pad's centre through
 * `nav` (`confirm`) and a tap anywhere: the whole screen is the target while
 * the window is open (a full-screen catcher; the pad stays above it, so its
 * centre still works and its directions do nothing). A tap is a completed one
 * (`isTap`: released soon, before a swipe's length); a swipe does nothing; and
 * the click that follows the tap is swallowed (`swallowNextClick`), so what
 * comes back under the finger when the window closes (the prompt, a shop's
 * row) does not act on it.
 */
import { type CSSProperties, type PointerEvent as ReactPointerEvent, useEffect, useRef } from 'react';
import type { Strings } from '@ab/session';
import { useModalFocus } from './focus.ts';
import { type MenuNav, type NavCommand, type PointerMark, isTap, navForKey, swallowNextClick } from './nav.ts';
import { Panel } from './panel.tsx';
import { text } from './text.ts';
import { uiTiming } from './timing.ts';
import './ui.css';

export type DialogueProps = {
  readonly strings: Strings;
  /** The speaker's name (already in the page's language); null for a line nobody says (a wish). */
  readonly speaker: string | null;
  readonly text: string;
  /** More pages follow this one (the ▼ shows). */
  readonly more: boolean;
  /** The next page, or the end of the dialogue on the last one. */
  readonly onAdvance: () => void;
  readonly onClose: () => void;
  readonly nav?: MenuNav;
};

const blink = { '--ab-blink-ms': `${uiTiming.dialoguePageMs}ms` } as CSSProperties;

export const Dialogue = ({ strings, speaker, text: line, more, onAdvance, onClose, nav }: DialogueProps) => {
  const current = useRef({ onAdvance, onClose });
  current.current = { onAdvance, onClose };
  const panel = useRef<HTMLElement>(null);
  useModalFocus(panel);
  useEffect(() => {
    const act = (command: NavCommand): void => {
      if (command === 'confirm') current.current.onAdvance();
      else if (command === 'close') current.current.onClose();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Tab') {
        // The window keeps the focus: nothing behind it is reachable while it is open.
        e.preventDefault();
        return;
      }
      const command = navForKey(e.key);
      if (command === null) return;
      e.preventDefault();
      if (e.repeat) return;
      act(command);
    };
    window.addEventListener('keydown', onKey, true);
    const off = nav?.subscribe(act);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      off?.();
    };
  }, [nav]);
  /** Where the primary pointer went down (on the catcher or the window, which then holds it until it is released). */
  const down = useRef<(PointerMark & { readonly id: number }) | null>(null);
  const press = (e: ReactPointerEvent<HTMLElement>): void => {
    e.preventDefault();
    if (!e.isPrimary || e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    down.current = { id: e.pointerId, x: e.clientX, y: e.clientY, t: e.timeStamp };
  };
  const release = (e: ReactPointerEvent<HTMLElement>): void => {
    const d = down.current;
    if (d === null || d.id !== e.pointerId) return;
    down.current = null;
    if (!isTap(d, { x: e.clientX, y: e.clientY, t: e.timeStamp })) return;
    e.preventDefault();
    swallowNextClick();
    current.current.onAdvance();
  };
  const cancel = (e: ReactPointerEvent<HTMLElement>): void => {
    if (down.current?.id === e.pointerId) down.current = null;
  };
  const pointer = { onPointerDown: press, onPointerUp: release, onPointerCancel: cancel };
  return (
    <>
      <div className="ab-veil" data-testid="dialogue-veil" {...pointer} />
      <Panel
        ref={panel}
        tabIndex={-1}
        placement="bottom"
        className="ab-dialogue"
        role="dialog"
        aria-modal="true"
        aria-label={text(strings, 'ui.dialogue.title')}
        data-testid="dialogue"
        style={blink}
        {...pointer}
      >
        {speaker !== null && (
          <p className="ab-dialogue-name" data-testid="dialogue-speaker">
            {speaker}
          </p>
        )}
        <p className="ab-dialogue-text" data-testid="dialogue-text">
          {line}
        </p>
        {more && (
          <span className="ab-dialogue-more" data-testid="dialogue-more" aria-hidden="true">
            {text(strings, 'ui.dialogue.more')}
          </span>
        )}
      </Panel>
    </>
  );
};
